import { VoicenterCredentials } from '../types/voicenter';
import EventsSDK, { LoginType, DebugOption, ExtensionEventReasonEnum } from '@voicenter-team/events-sdk';
import { LocalStorageWorker } from '@voicenter-team/socketio-storage-logger';

// Voicenter SDK wrapper class
class VoicenterSDKWrapper {
  private sdk: InstanceType<typeof EventsSDK> | null = null;
  private isInitialized = false;
  private isConnected = false;
  private connectionStatus: 'disconnected' | 'connecting' | 'connected' | 'error' = 'disconnected';
  private eventListeners: Map<string, Set<(data: any) => void>> = new Map();

  constructor(private credentials: VoicenterCredentials) {}

  getConnectionStatus(): 'disconnected' | 'connecting' | 'connected' | 'error' {
    return this.connectionStatus;
  }

  isSDKConnected(): boolean {
    return this.isConnected;
  }

  private handleCallEvent(eventType: 'call.start' | 'call.end', data: any): void {
    // Trigger any registered event listeners
    const listeners = this.eventListeners.get('ExtensionEvent');
    if (listeners) {
      listeners.forEach(callback => {
        callback({
          type: eventType,
          data: data,
          timestamp: new Date()
        });
      });
    }
  }

  async init(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // Create SDK instance directly using npm package
      console.log('Creating Voicenter SDK instance with user credentials');
      
      // According to TypeScript definitions, use loginType, email, and password
      // storageWorker should be passed as a class (LocalStorageWorker), not an instance
      // storageWorker is required at runtime but not in TypeScript definitions
      // Using type assertion for this specific property
      this.sdk = new EventsSDK({
        loginType: LoginType.USER,
        email: this.credentials.username,
        password: this.credentials.password,
        timeout: 10000,
        reconnectionDelay: 10000,
        protocol: 'https',
        debugOption: import.meta.env.DEV ? DebugOption.FULL : undefined,
        storageWorker: LocalStorageWorker,
      } as any);

      // Initialize the SDK first to set up the event system
      console.log('Initializing Voicenter SDK...');
      this.connectionStatus = 'connecting';
      
      try {
        await this.sdk.init();
        console.log('SDK initialized successfully');
        
        // Set up event listeners immediately after init() resolves
        this.setupEventListeners();
        
        this.isInitialized = true;
      } catch (initError: any) {
        console.error('SDK init() failed:', initError);
        this.connectionStatus = 'error';
        // Check if it's a 404 or authentication error
        if (initError?.message?.includes('404') || initError?.message?.includes('Not Found')) {
          throw new Error('Voicenter API endpoint not found. Please verify your credentials are correct and the API is accessible.');
        }
        if (initError?.message?.includes('AccessToken')) {
          throw new Error('Voicenter login failed: Invalid credentials or authentication error. Please check your username and password.');
        }
        throw initError;
      }
      
    } catch (error: any) {
      console.error('Failed to initialize Voicenter SDK:', error);
      console.error('Error details:', {
        message: error?.message,
        stack: error?.stack,
        name: error?.name
      });
      throw error;
    }
  }

  private setupEventListeners(): void {
    if (!this.sdk) {
      console.warn('Cannot setup event listeners: SDK instance is null');
      return;
    }

    // Helper function to safely register event listeners with better error handling
    // Using type assertion for event names that may not be in strict TypeScript types
    const safeOn = (event: string, handler: (data: any) => void) => {
      try {
        if (!this.sdk) {
          console.warn(`Cannot register listener for ${event}: SDK instance is null`);
          return false;
        }
        
        if (typeof this.sdk.on !== 'function') {
          console.warn(`Cannot register listener for ${event}: SDK.on is not a function`);
          return false;
        }
        
        // Type assertion needed for events not in strict EventDataMap
        (this.sdk.on as any)(event, handler);
        console.log(`Event listener registered for: ${event}`);
        return true;
      } catch (error: any) {
        console.error(`Failed to register listener for ${event}:`, error);
        return false;
      }
    };

    // Connection state management events
    safeOn('connect', () => {
      console.log('Voicenter SDK connected');
      this.isConnected = true;
      this.connectionStatus = 'connected';
      window.dispatchEvent(new CustomEvent('VoicenterConnectionStatus', { 
        detail: { status: 'connected' } 
      }));
    });

    safeOn('disconnect', (reason: any) => {
      console.log('Voicenter SDK disconnected:', reason);
      this.isConnected = false;
      this.connectionStatus = 'disconnected';
      window.dispatchEvent(new CustomEvent('VoicenterConnectionStatus', { 
        detail: { status: 'disconnected', reason } 
      }));
    });

    safeOn('connect_error', (error: any) => {
      console.error('Voicenter SDK connection error:', error);
      this.connectionStatus = 'error';
      window.dispatchEvent(new CustomEvent('VoicenterConnectionStatus', { 
        detail: { status: 'error', error } 
      }));
    });

    // Reconnection events
    safeOn('reconnect', (attemptNumber: number) => {
      console.log('Voicenter SDK reconnected after', attemptNumber, 'attempts');
      this.isConnected = true;
      this.connectionStatus = 'connected';
      window.dispatchEvent(new CustomEvent('VoicenterConnectionStatus', { 
        detail: { status: 'connected', reconnect: true, attemptNumber } 
      }));
    });

    safeOn('reconnect_attempt', (attemptNumber: number) => {
      console.log('Voicenter SDK reconnection attempt:', attemptNumber);
      this.connectionStatus = 'connecting';
      window.dispatchEvent(new CustomEvent('VoicenterConnectionStatus', { 
        detail: { status: 'connecting', reconnectAttempt: attemptNumber } 
      }));
    });

    safeOn('reconnect_error', (error: any) => {
      console.error('Voicenter SDK reconnection error:', error);
      this.connectionStatus = 'error';
    });

    safeOn('reconnect_failed', () => {
      console.error('Voicenter SDK reconnection failed');
      this.isConnected = false;
      this.connectionStatus = 'error';
      window.dispatchEvent(new CustomEvent('VoicenterConnectionStatus', { 
        detail: { status: 'error', reconnectFailed: true } 
      }));
    });

    // Login events
    safeOn('loginSuccess', (response: any) => {
      console.log('Voicenter login successful:', response);
      this.isConnected = true;
      this.connectionStatus = 'connected';
      window.dispatchEvent(new CustomEvent('VoicenterLoginSuccess', { detail: response }));
    });

    safeOn('loginStatus', (status: any) => {
      console.log('Voicenter login status:', status);
      window.dispatchEvent(new CustomEvent('VoicenterLoginStatus', { detail: status }));
    });

    // Extension events
    safeOn('ExtensionEvent', (response: any) => {
      console.log('Voicenter ExtensionEvent received:', response);
      
      // Forward event to the app via a DOM CustomEvent for easy subscription in UI components
      try {
        window.dispatchEvent(new CustomEvent('VoicenterExtensionEvent', { detail: response }));
      } catch (e) {
        console.warn('Failed to dispatch VoicenterExtensionEvent:', e);
      }
      
      // Handle call events using the official ExtensionEventReasonEnum from the SDK.
      // ANSWER fires when the call is actually answered/accepted (NOT NEWCALL/dial).
      if (response.data && response.data.reason) {
        const reason = response.data.reason as ExtensionEventReasonEnum;
        console.log('Extension event reason:', reason);

        if (reason === ExtensionEventReasonEnum.ANSWER) {
          console.log('Call answered/accepted - starting call');
          this.handleCallEvent('call.start', response);
        } else if (reason === ExtensionEventReasonEnum.NEWCALL) {
          console.log('New call initiated (ringing) - waiting for answer');
        } else if (reason === ExtensionEventReasonEnum.HANGUP) {
          console.log('Call ended event detected');
          this.handleCallEvent('call.end', response);
        } else if (reason === ExtensionEventReasonEnum.USER_STATUS_UPDATE) {
          console.log('User status update event detected');
        }
      }
    });

    safeOn('AllExtensionsStatus', (response: any) => {
      console.log('Voicenter AllExtensionsStatus received:', response);
      window.dispatchEvent(new CustomEvent('VoicenterAllExtensionsStatus', { detail: response }));
    });

    // Keepalive events for connection health monitoring
    safeOn('keepalive', () => {
      console.log('Voicenter SDK keepalive sent');
    });

    safeOn('keepaliveResponse', (response: any) => {
      console.log('Voicenter SDK keepalive response:', response);
    });

    // Note: Error and loginError listeners are registered after login() succeeds
    // to avoid "Cannot read properties of undefined (reading 'push')" errors
  }

  private setupErrorListeners(): void {
    if (!this.sdk) {
      return;
    }

    const safeOn = (event: string, handler: (data: any) => void) => {
      try {
        if (this.sdk && typeof this.sdk.on === 'function') {
          // Type assertion needed for events not in strict EventDataMap
          (this.sdk.on as any)(event, handler);
          console.log(`Error event listener registered for: ${event}`);
          return true;
        }
        return false;
      } catch (error: any) {
        console.error(`Failed to register error listener for ${event}:`, error);
        return false;
      }
    };

    // Register error listeners after successful initialization and login
    safeOn('error', (error: any) => {
      console.error('Voicenter SDK error:', error);
      this.connectionStatus = 'error';
      window.dispatchEvent(new CustomEvent('VoicenterError', { detail: error }));
    });

    safeOn('loginError', (error: any) => {
      console.error('Voicenter login error:', error);
      this.connectionStatus = 'error';
      window.dispatchEvent(new CustomEvent('VoicenterLoginError', { detail: error }));
    });
  }

  async login(): Promise<void> {
    if (!this.isInitialized) {
      await this.init();
    }

    if (!this.sdk) {
      throw new Error('SDK not initialized');
    }

    // Call login explicitly with 'user' as per documentation
    console.log('Calling SDK login with user credentials...');
    this.connectionStatus = 'connecting';
    
    try {
      // The SDK login is accessed via authClass property
      // Based on documentation, login() is called without parameters for user login type
      await this.sdk.authClass.login();
      console.log('SDK login called successfully');
      
      // Set up error listeners after successful login
      // This avoids "Cannot read properties of undefined (reading 'push')" errors
      this.setupErrorListeners();
    } catch (loginError: any) {
      console.error('SDK login failed:', loginError);
      this.connectionStatus = 'error';
      throw new Error(`Voicenter login failed: ${loginError?.message || 'Unknown error'}`);
    }
  }

  async logout(): Promise<void> {
    if (this.sdk) {
      try {
        // Use disconnect() instead of logout() as logout doesn't exist in SDK
        await this.sdk.disconnect();
        console.log('Voicenter SDK: Disconnected');
        this.isConnected = false;
        this.connectionStatus = 'disconnected';
      } catch (error: any) {
        console.error('Voicenter disconnect failed:', error);
      }
    }
  }

  on(event: string, callback: (data: any) => void): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event)!.add(callback);

    // Also register with the actual SDK
    if (this.sdk) {
      // Type assertion for events not in strict EventDataMap
      (this.sdk.on as any)(event, callback);
    }
  }

  off(event: string, callback: (data: any) => void): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.delete(callback);
    }

    // Also unregister from the actual SDK via eventEmitterClass
    if (this.sdk) {
      try {
        // Use eventEmitterClass.off() which has the off method
        (this.sdk.eventEmitterClass.off as any)(event, callback);
      } catch (error) {
        console.warn(`Error removing listener for event ${event}:`, error);
      }
    }
  }

  destroy(): void {
    console.log('VoicenterSDKWrapper destroy() called');
    if (this.sdk) {
      try {
        console.log('Cleaning up event listeners...');
        // Clean up all event listeners
        this.eventListeners.forEach((listeners, event) => {
          listeners.forEach(callback => {
            try {
              // Use eventEmitterClass.off() which has the off method
              if (this.sdk) {
                (this.sdk.eventEmitterClass.off as any)(event, callback);
              }
            } catch (error) {
              console.warn(`Error removing listener for event ${event}:`, error);
            }
          });
        });
        
        console.log('Disconnecting SDK instance...');
        // Use disconnect() instead of destroy() as destroy() may not exist
        this.sdk.disconnect().catch((error: any) => {
          console.warn('Error disconnecting SDK:', error);
        });
        console.log('SDK instance disconnected');
      } catch (error: any) {
        console.error('Error destroying Voicenter SDK:', error);
        console.error('Error details:', {
          message: error?.message,
          stack: error?.stack,
          name: error?.name
        });
      }
    } else {
      console.log('No SDK instance to destroy');
    }

    this.eventListeners.clear();
    this.sdk = null;
    this.isInitialized = false;
    this.isConnected = false;
    this.connectionStatus = 'disconnected';
    console.log('VoicenterSDKWrapper cleanup completed');
  }
}

export const createVoicenterSDK = (credentials: VoicenterCredentials): VoicenterSDKWrapper => {
  return new VoicenterSDKWrapper(credentials);
};